'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import Input from '../../../atoms/Input/Input';
import { getValidationResponse, hasValue } from '../utils';
import { useFormContext } from '../FormContext';
import type { CurrencyField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  UseFormGetValues,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type CurrencyControlProps = {
  field: CurrencyField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  locale: string;
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  errors: FieldErrors<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
};

/**
 * Resolves the decimal and thousands separator characters for a given locale.
 * Uses `Intl.NumberFormat` to ensure accuracy across all locales.
 * @param locale - BCP 47 locale string (e.g. 'en-US', 'de-DE')
 * @returns Object with `decimal` and `thousands` separator characters
 */
const getLocaleSeparators = (locale: string) => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89);
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  const thousands = parts.find((p) => p.type === 'group')?.value ?? ',';
  return { decimal, thousands };
};

/**
 * Parses a locale-formatted currency string into a raw numeric string.
 * Strips thousands separators and normalises the decimal separator to '.'.
 * @param value - The display string (e.g. '1.234,56' for de-DE)
 * @param locale - BCP 47 locale string
 * @returns A plain numeric string (e.g. '1234.56') or empty string
 */
const parseCurrencyValue = (value: string, locale: string): string => {
  if (!value) return '';
  const { decimal, thousands } = getLocaleSeparators(locale);

  let cleaned = value;
  // Strip thousands separators
  if (thousands) {
    cleaned = cleaned.split(thousands).join('');
  }
  // Normalise decimal separator to '.'
  if (decimal !== '.') {
    cleaned = cleaned.replace(decimal, '.');
  }
  // Remove anything that isn't a digit, dot, or leading minus
  cleaned = cleaned.replace(/[^\d.-]/g, '');

  // Validate it's a real number
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return cleaned;
  const parsed = Number(cleaned);
  if (Number.isNaN(parsed)) return '';
  return cleaned;
};

/**
 * Formats a raw numeric string into a locale-formatted display string.
 * Used on blur to add thousands separators and proper decimal formatting.
 * @param value - Plain numeric string (e.g. '1234.56')
 * @param locale - BCP 47 locale string
 * @param decimalPlaces - Maximum number of decimal places
 * @returns Locale-formatted string
 */
const formatCurrencyDisplay = (value: string, locale: string, decimalPlaces: number): string => {
  if (!value || value === '-') return value;

  // Clean format
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimalPlaces,
  }).format(num);
};

const isDependencyUnmet = (
  config: CurrencyField,
  getValues: UseFormGetValues<FieldValues>
): boolean => {
  if (!config.dependency) return false;
  const depValue = getValues(config.dependency.name);
  return Array.isArray(depValue)
    ? !depValue.includes(config.dependency.value)
    : depValue !== config.dependency.value;
};

/** Required, pattern and min/max checks; the last three only apply once there is a value. */
const getRuleError = (value: unknown, config: CurrencyField): string | undefined => {
  if (config.required && (!value || value === '')) {
    return config.validationMessages?.required || 'This field is required';
  }
  if (!value) return undefined;
  if (config.pattern && !config.pattern.test(String(value))) {
    return config.validationMessages?.pattern || 'Invalid value';
  }
  if (config.min !== undefined && Number(value) < config.min) {
    return config.validationMessages?.min || `Value must be at least ${config.min}`;
  }
  if (config.max !== undefined && Number(value) > config.max) {
    return config.validationMessages?.max || `Value must be at most ${config.max}`;
  }
  return undefined;
};

/**
 * Form control for locale-aware currency input fields.
 * Shows raw input while typing and formats with thousands separators on blur.
 * On focus, strips formatting back to raw input for easy editing.
 *
 * Stores the raw numeric value (e.g. '1234.56') in the form state regardless
 * of locale display format. On blur it cleans up trailing decimals/zeros and
 * adds locale-appropriate thousands separators.
 *
 * @component
 * @param {CurrencyControlProps} props - Component props
 * @returns {JSX.Element} Currency control component
 *
 * @example
 * ```tsx
 * // In form field config:
 * {
 *   id: 'amount',
 *   fieldType: 'currency',
 *   name: 'amount',
 *   apiKey: 'amount',
 *   label: 'Amount (€)',
 *   required: true,
 *   decimalPlaces: 2,
 * }
 * ```
 */
const CurrencyControl = ({
  field,
  theme,
  direction,
  locale,
  register,
  setValue,
  watch,
  getValues,
  errors,
  trigger: _trigger,
}: CurrencyControlProps) => {
  const { disabled: formDisabled } = useFormContext();
  const decimalPlaces = field.decimalPlaces ?? 2;
  const formValue = watch(field.name) as string | undefined;

  // Watch the dependency field to disable this control when the other has a value
  const depFieldName = field.disabledDependency?.name;
  const depValue = watch(depFieldName ?? '') as unknown;
  const isDisabledByDependency = depFieldName ? hasValue(depValue) : false;
  const isDisabled = field.disabled || isDisabledByDependency || formDisabled;

  // Flag to skip the sync effect when the change came from user typing
  const isInternalChange = useRef(false);

  // Validation is registered once, so it reads the latest config (e.g. limits loaded later) from here.
  const latestField = useRef(field);
  latestField.current = field;

  // The display value shown in the input
  const [displayValue, setDisplayValue] = useState('');

  // Sync display value when form value changes externally (e.g. resetFormWithValues, defaultValue)
  // Skip when the change came from handleChange (user typing)
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const raw = String(formValue ?? '');
    if (!raw) {
      setDisplayValue('');
      return;
    }
    const formatted = formatCurrencyDisplay(raw, locale, decimalPlaces);
    setDisplayValue(formatted);
  }, [formValue, locale, decimalPlaces]);

  // Register field with react-hook-form for validation
  useEffect(() => {
    register(field.name, {
      validate: (value) => {
        const config = latestField.current;
        if (isDependencyUnmet(config, getValues)) return true;
        return getRuleError(value, config) ?? config.validate?.(value) ?? true;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFocus = useCallback(() => {
    // Strip formatting back to raw value using locale decimal separator
    // so the user can edit the number without thousands separators getting in the way
    const raw = parseCurrencyValue(displayValue, locale);
    if (raw) {
      const { decimal } = getLocaleSeparators(locale);
      // Show raw value with locale decimal separator (no thousands separators)
      setDisplayValue(decimal !== '.' ? raw.replace('.', decimal) : raw);
    }
  }, [displayValue, locale]);

  const handleBlur = useCallback(() => {
    // Clean format: strip trailing dot/zeros and truncate to allowed decimal places
    let raw = parseCurrencyValue(displayValue, locale).replace(/\.$/, '');
    const dotIdx = raw.indexOf('.');
    if (dotIdx !== -1 && raw.length - dotIdx - 1 > decimalPlaces) {
      raw = raw.slice(0, dotIdx + 1 + decimalPlaces);
    }
    // Mark as internal so the sync effect doesn't overwrite our display
    isInternalChange.current = true;
    setValue(field.name, raw, { shouldDirty: true, shouldValidate: true });
    const formatted = raw ? formatCurrencyDisplay(raw, locale, decimalPlaces) : '';
    setDisplayValue(formatted);
  }, [displayValue, locale, field.name, decimalPlaces, setValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const { decimal, thousands } = getLocaleSeparators(locale);

      // Allow digits, one decimal separator, and leading minus
      const escapedDec = decimal === '.' ? '\\.' : decimal;
      const allowedChars = new RegExp(`^-?[0-9]*[${escapedDec}]?[0-9]*$`);

      // Strip thousands separators from input (user may paste formatted text)
      const stripped = inputValue.split(thousands).join('');

      if (stripped === '' || allowedChars.test(stripped)) {
        // Parse to raw numeric string
        const raw = parseCurrencyValue(stripped, locale);

        // Display the raw input as-is (no live formatting)
        setDisplayValue(stripped);
        // Mark as internal so the sync effect doesn't overwrite our display
        isInternalChange.current = true;
        setValue(field.name, raw, { shouldDirty: true });
      }

      if (field.onChange) {
        field.onChange(parseCurrencyValue(inputValue, locale));
      }
    },
    [locale, field, setValue]
  );

  return (
    <Input
      theme={theme}
      key={`form_field_${field.name}`}
      type="currency"
      name={field.name}
      label={field.label}
      helperText={field.helperText}
      placeholder={field.placeholder}
      required={field.required}
      disabled={isDisabled}
      controlled
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      inputProps={{ onFocus: handleFocus }}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
    />
  );
};

export default CurrencyControl;
